import { useRef } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { type Injectables, Remote } from '@proposify/mfe-tools';
import { trackError } from '@proposify/frontend-observability';
import { NotificationsProvider } from 'components/notifications/NotificationsProvider';
import { I18nProvider } from 'providers/I18nProvider';
import { QueryProvider } from 'providers/QueryProvider';
import { QueryProviderV5 } from 'providers/QueryProviderV5';
import { DesignTokenProvider, designToken } from '@proposify/ui';
import { TemplatePreview } from 'pages/template-preview/TemplatePreview';

export interface TemplatePreviewInjectables extends Injectables {
  contentId: string;
}

const REQUIRED_ENV_VARS = ['VITE_DOCUMENT_SERVICE_API_ENDPOINT', 'VITE_DOCUMENT_SERVICE_API_HOST'] as const;

function assertRequiredEnvVars(): void {
  const missing = REQUIRED_ENV_VARS.filter((key) => !globalThis.env?.[key]);
  if (missing.length > 0) {
    trackError(new Error(`TemplatePreviewMicrofrontend: missing required env vars: ${missing.join(', ')}`));
  }
}

function validateInjectables(injectables: object): asserts injectables is TemplatePreviewInjectables {
  if ('contentId' in injectables) {
    if (typeof injectables.contentId === 'string' && injectables.contentId !== '') {
      return;
    }
  }
  const error = new Error('validation failed, could not load TemplatePreviewMicrofrontend');
  trackError(error, {
    injectablesFromHost: injectables,
  });
  throw error;
}

const TemplatePreviewMicrofrontend = () => {
  const envAssertedRef = useRef(false);
  if (!envAssertedRef.current) {
    envAssertedRef.current = true;
    assertRequiredEnvVars();
  }

  return (
    <Remote name="template-preview-remote" validateInjectables={validateInjectables}>
      {(injectables) => (
        // HelmetProvider is required by react-helmet-async and is added independently in each
        // MFE entrypoint, since this app has no single shared root the provider tree can live in.
        <HelmetProvider>
          <I18nProvider>
            <QueryProvider>
              <QueryProviderV5>
                <DesignTokenProvider theme={designToken}>
                  <NotificationsProvider>
                    <TemplatePreview contentId={injectables.contentId} />
                  </NotificationsProvider>
                </DesignTokenProvider>
              </QueryProviderV5>
            </QueryProvider>
          </I18nProvider>
        </HelmetProvider>
      )}
    </Remote>
  );
};

const TemplatePreviewMicrofrontendEntry = () => <TemplatePreviewMicrofrontend />;
export default TemplatePreviewMicrofrontendEntry;
